import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Hourglass, Plus, HelpCircle, Download, ShieldAlert, ShieldCheck, History } from "lucide-react";
import api, { formatApiErrorDetail, openServerPdf } from "../../lib/api";
import { Card, Button, Field, Input, Select, Spinner } from "../../components/ui-kit";
import { ShelfLifeResultBadge as ResultBadge, ShelfLifeWhyDrawer as WhyDrawer } from "../../components/ShelfLifeWhyDrawer";
import ShelfLifeCategoryWizard from "../../components/ShelfLifeCategoryWizard";
import AcademyTitleBadge from "../../academy/AcademyTitleBadge";

export default function ShelfLifeExpert() {
  const [catalog, setCatalog] = useState(null);
  const [products, setProducts] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState("");
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creatingProduct, setCreatingProduct] = useState(false);
  const [showCategoryWizard, setShowCategoryWizard] = useState(false);
  const [productForm, setProductForm] = useState({ name: "", food_category_id: "" });
  const [assessForm, setAssessForm] = useState({
    process_type_id: "", packaging_id: "", storage_condition_id: "",
    temp_c: "", ph: "", aw: "", preservatives: "",
  });
  const [assessing, setAssessing] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [whyAssessment, setWhyAssessment] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: cat }, { data: prod }] = await Promise.all([
        api.get("/app/shelf-life/catalog"), api.get("/app/shelf-life/products"),
      ]);
      setCatalog(cat);
      setProducts(prod.products);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const loadAssessments = async (productId) => {
    if (!productId) { setAssessments([]); return; }
    try {
      const { data } = await api.get(`/app/shelf-life/assessments?product_id=${productId}`);
      setAssessments(data.assessments);
      setLastResult(data.assessments[0] || null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  useEffect(() => { loadAssessments(selectedProductId); }, [selectedProductId]);

  const selectedProduct = products.find((p) => p.id === selectedProductId);

  const createProduct = async () => {
    if (!productForm.name.trim() || !productForm.food_category_id) { toast.error("Indica nome e categoria alimentare del prodotto"); return; }
    try {
      const { data } = await api.post("/app/shelf-life/products", productForm);
      toast.success("Prodotto creato");
      setCreatingProduct(false);
      setProductForm({ name: "", food_category_id: "" });
      setProducts((prev) => [data.product, ...prev]);
      setSelectedProductId(data.product.id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const runAssessment = async () => {
    if (!selectedProductId) { toast.error("Seleziona prima un prodotto"); return; }
    setAssessing(true);
    try {
      const body = {
        product_id: selectedProductId,
        process_type_id: assessForm.process_type_id || null,
        packaging_id: assessForm.packaging_id || null,
        storage_condition_id: assessForm.storage_condition_id || null,
        temp_c: assessForm.temp_c === "" ? null : Number(assessForm.temp_c),
        ph: assessForm.ph === "" ? null : Number(assessForm.ph),
        aw: assessForm.aw === "" ? null : Number(assessForm.aw),
        preservative_keys: assessForm.preservatives.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean),
      };
      const { data } = await api.post("/app/shelf-life/assess", body);
      setLastResult(data.assessment);
      setAssessments((prev) => [data.assessment, ...prev]);
      toast.success("Valutazione calcolata");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setAssessing(false);
    }
  };

  const downloadPdf = (assessmentId) => {
    openServerPdf(`/app/shelf-life/assessments/${assessmentId}/pdf`);
  };

  if (loading) return <Spinner />;

  return (
    <div className="space-y-6" data-testid="shelf-life-page">
      <div className="flex items-center gap-3">
        <div className="grid h-11 w-11 place-items-center rounded-xl bg-navy text-gold-bright"><Hourglass className="h-5 w-5" /></div>
        <div>
          <div className="flex flex-wrap items-center gap-2"><h1 className="font-heading text-xl font-bold text-navy">DF Food Safety Expert Engine — Shelf life</h1><AcademyTitleBadge /></div>
          <p className="text-sm text-slate-500">Motore a punteggio per la durata dei prodotti, con motivazione tecnica e riferimenti normativi.</p>
        </div>
      </div>

      <Card className="p-5">
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-full sm:w-auto sm:flex-1 sm:min-w-[220px]">
            <Field label="Prodotto">
              <Select value={selectedProductId} onChange={(e) => setSelectedProductId(e.target.value)} data-testid="shelf-life-product-select">
                <option value="">Seleziona un prodotto...</option>
                {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </Select>
            </Field>
          </div>
          <Button variant="outline" onClick={() => setCreatingProduct(true)} data-testid="shelf-life-new-product"><Plus className="w-4 h-4" /> Nuovo prodotto</Button>
        </div>

        {creatingProduct && (
          <div className="mt-4 grid grid-cols-1 gap-3 rounded-lg border border-hairline p-4 sm:grid-cols-3">
            <Field label="Nome prodotto *"><Input value={productForm.name} onChange={(e) => setProductForm({ ...productForm, name: e.target.value })} /></Field>
            <Field label="Categoria alimentare *">
              <div className="flex gap-2">
                <Select value={productForm.food_category_id} onChange={(e) => setProductForm({ ...productForm, food_category_id: e.target.value })}>
                  <option value="">Seleziona...</option>
                  {catalog.categories.map((c) => <option key={c.id} value={c.id}>{c.name}{c.high_risk ? " (rischio elevato)" : ""}</option>)}
                </Select>
                <Button type="button" variant="outline" className="shrink-0 px-3" onClick={() => setShowCategoryWizard(true)}>?</Button>
              </div>
              <span className="mt-1 block text-[11px] text-slate-400">Non sai quale scegliere? Clicca "?".</span>
            </Field>
            <div className="flex items-end gap-2">
              <Button variant="success" onClick={createProduct}>Salva prodotto</Button>
              <Button variant="outline" onClick={() => setCreatingProduct(false)}>Annulla</Button>
            </div>
          </div>
        )}
      </Card>
      {showCategoryWizard && (
        <ShelfLifeCategoryWizard
          onClose={() => setShowCategoryWizard(false)}
          onApply={(categoryId) => { setProductForm({ ...productForm, food_category_id: categoryId }); setShowCategoryWizard(false); toast.success("Categoria applicata"); }}
        />
      )}

      {selectedProduct && (
        <Card className="p-5">
          <h2 className="font-heading text-base font-semibold text-navy mb-3">Nuova valutazione — {selectedProduct.name}</h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Field label="Tipo di processo">
              <Select value={assessForm.process_type_id} onChange={(e) => setAssessForm({ ...assessForm, process_type_id: e.target.value })}>
                <option value="">Non specificato</option>
                {catalog.process_types.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </Select>
            </Field>
            <Field label="Imballaggio">
              <Select value={assessForm.packaging_id} onChange={(e) => setAssessForm({ ...assessForm, packaging_id: e.target.value })}>
                <option value="">Non specificato</option>
                {catalog.packaging_types.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </Select>
            </Field>
            <Field label="Conservazione">
              <Select value={assessForm.storage_condition_id} onChange={(e) => setAssessForm({ ...assessForm, storage_condition_id: e.target.value })}>
                <option value="">Non specificata</option>
                {catalog.storage_conditions.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </Select>
            </Field>
            <Field label="Temperatura effettiva (°C)"><Input type="number" step="0.1" value={assessForm.temp_c} onChange={(e) => setAssessForm({ ...assessForm, temp_c: e.target.value })} /></Field>
            <Field label="pH"><Input type="number" step="0.01" value={assessForm.ph} onChange={(e) => setAssessForm({ ...assessForm, ph: e.target.value })} /></Field>
            <Field label="Attività dell'acqua (aw)"><Input type="number" step="0.01" value={assessForm.aw} onChange={(e) => setAssessForm({ ...assessForm, aw: e.target.value })} /></Field>
            <div className="sm:col-span-3">
              <Field label="Conservanti dichiarati" hint="Chiavi separate da virgola, es. nitriti, lattato">
                <Input value={assessForm.preservatives} onChange={(e) => setAssessForm({ ...assessForm, preservatives: e.target.value })} />
              </Field>
            </div>
          </div>
          <div className="mt-4">
            <Button variant="accent" loading={assessing} onClick={runAssessment} data-testid="shelf-life-run-assessment">Calcola shelf life</Button>
          </div>
        </Card>
      )}

      {lastResult && (
        <Card className="p-5" data-testid="shelf-life-result">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-3xl font-bold text-navy">{lastResult.result.shelf_life_days ?? "n.d."} <span className="text-base font-normal text-slate-500">giorni</span></div>
              <div className="text-sm text-slate-500">{lastResult.result.evidence_level_label} — fiducia {lastResult.result.confidence_score}/100</div>
            </div>
            <div className="flex items-center gap-2">
              <ResultBadge needsValidation={lastResult.result.needs_validation} />
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setWhyAssessment(lastResult)} data-testid="shelf-life-why-button"><HelpCircle className="w-4 h-4" /> Perché questa shelf life?</Button>
            <Button variant="outline" onClick={() => downloadPdf(lastResult.id)}><Download className="w-4 h-4" /> Scarica PDF</Button>
          </div>
          {lastResult.result.needs_validation && (
            <div className="mt-3 flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
              <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" /> Questo risultato richiede la validazione di un tecnologo alimentare o microbiologo prima dell'uso in etichetta.
            </div>
          )}
        </Card>
      )}

      {selectedProduct && assessments.length > 0 && (
        <Card className="p-5">
          <h2 className="mb-3 flex items-center gap-2 font-heading text-base font-semibold text-navy"><History className="w-4 h-4" /> Storico valutazioni</h2>
          <div className="space-y-2">
            {assessments.map((a) => (
              <div key={a.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-hairline p-3 text-sm">
                <div>
                  <div className="font-medium text-navy">{a.result.shelf_life_days ?? "n.d."} giorni — {a.result.evidence_level_label}</div>
                  <div className="text-xs text-slate-400">{new Date(a.created_at).toLocaleString("it-IT")}</div>
                </div>
                <div className="flex items-center gap-2">
                  <ResultBadge needsValidation={a.result.needs_validation} />
                  <Button variant="ghost" onClick={() => setWhyAssessment(a)}><HelpCircle className="w-4 h-4" /></Button>
                  <Button variant="ghost" onClick={() => downloadPdf(a.id)}><Download className="w-4 h-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <WhyDrawer assessment={whyAssessment} onClose={() => setWhyAssessment(null)} />
    </div>
  );
}
